import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { createRequiredNumberSchema } from "@/pages/workflows/edit/actions/workflow-edit-action-validation.utils.ts";
import { WorkflowBlockConditionsList } from "@/pages/workflows/edit/flow/WorkflowBlockConditionsList.tsx";
import { useWorkflowBlockConditions } from "@/pages/workflows/edit/flow/use-workflow-block-conditions.tsx";
import { useApprovalCategories } from "@/queries/approval/use-approval-categories.query.ts";
import { useSurveyTemplateSearch } from "@/queries/surveys/use-survey-templates.query.ts";
import { useWorkflowUpdateBlockActionApproval } from "@/queries/workflows/workflow.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiApprovalCategoryType } from "@/types/approvals/approval-policy.types.ts";
import { ApiFormTemplateFilter } from "@/types/forms/form-template.types.ts";
import { ApiWorkflowBlockActionTypes } from "@/types/workflows/workflow.base-enum.types.ts";
import { ApiWorkflowBlock, ApiWorkflowVersion } from "@/types/workflows/workflow.types.ts";
import {
  ApiWorkflowBlockActionApproval,
  ApiWorkflowBlockActionApprovalSave,
  getWorkflowApprovalWriteResponsesTypeItems,
  WorkflowApprovalWriteResponsesType,
} from "@/types/workflows/workflow.action-notification.types.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  Input,
  Modal,
  ModalProps,
  Select,
  Stack,
  TabItem,
  Tabs,
  Textarea,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { WorkflowActionTimingEditor } from "./WorkflowActionTimingEditor.tsx";
import {
  getWorkflowActionTimingPayload,
  getWorkflowActionTimingValues,
  useWorkflows,
  WorkflowActionTimingFormFields,
} from "@/pages/workflows/workflow-hook.tsx";

export interface WorkflowEditActionApprovalModalProps extends ModalProps {
  workflowVersion: ApiWorkflowVersion;
  block: ApiWorkflowBlock;
  actionApproval: ApiWorkflowBlockActionApproval | null;
}

interface ApprovalFormValues
  extends ApiWorkflowBlockActionApprovalSave, WorkflowActionTimingFormFields {
  block_title: string | null;
}

export function WorkflowEditActionApprovalModal({
  workflowVersion,
  block,
  actionApproval,
  ...props
}: WorkflowEditActionApprovalModalProps) {
  const { close } = useModals();
  const { id } = { ...props };
  const { t } = useTranslation();
  const { workflowActionTimingSchemaFields } = useWorkflows();
  const [activeTab, setActiveTab] = useState(0);
  const { data: categories, isLoading: isCategoriesLoading } = useApprovalCategories();
  const [templateFilter, setTemplateFilter] = useState<ApiFormTemplateFilter>({
    page_number: 1,
    text: "",
  });
  const { data: surveyTemplates, isLoading: isTemplatesLoading } = useSurveyTemplateSearch(
    templateFilter,
    true
  );
  const schemaObject = yup.object({
    block_title: yup.string().defined().nullable(),
    ...workflowActionTimingSchemaFields,
    category_id: createRequiredNumberSchema(
      t("errors:field_required"),
      t("errors:field_number_required")
    ).min(1, t("errors:field_required")),
    survey_template_id: createRequiredNumberSchema(
      t("errors:field_required"),
      t("errors:field_number_required")
    ).min(1, t("errors:field_required")),
    public_title: yup.string().defined().nullable(),
    public_description: yup.string().defined().nullable(),
    when_to_save_responses: yup
      .mixed<WorkflowApprovalWriteResponsesType>()
      .required(t("errors:field_required")),
  });

  const { conditions, handleAddCondition, handleEditCondition, handleDeleteCondition } =
    useWorkflowBlockConditions(workflowVersion, block);

  const customCategories = useMemo(
    () => (categories || []).filter((category) => category.type === ApiApprovalCategoryType.custom),
    [categories]
  );

  const categoryOptions = useMemo(() => {
    return customCategories.map((category) => ({
      id: category.id,
      title: category.title,
    }));
  }, [customCategories]);

  const templateOptions = useMemo(
    () =>
      surveyTemplates?.items.map((template) => ({
        id: template.id,
        title: template.title,
      })) ?? [],
    [surveyTemplates]
  );

  const {
    control,
    handleSubmit,
    register,
    setValue,
    formState: { errors },
  } = useForm<ApprovalFormValues>({
    mode: "onSubmit",
    resolver: yupResolver(schemaObject),
    values: {
      block_title: block.title ?? "",
      ...getWorkflowActionTimingValues(block),
      category_id: actionApproval?.category_id ?? 0,
      survey_template_id: actionApproval?.survey_template_id ?? 0,
      public_title: actionApproval?.public_title ?? null,
      public_description: actionApproval?.public_description ?? null,
      when_to_save_responses:
        actionApproval?.when_to_save_responses ?? WorkflowApprovalWriteResponsesType.after_approval,
    },
  });

  const updateBlockActionApproval = useWorkflowUpdateBlockActionApproval(control);

  const handleSave = handleSubmit(async (data) => {
    try {
      await updateBlockActionApproval.mutateAsync({
        workflowId: workflowVersion.workflow_id,
        workflowVersionId: workflowVersion.id,
        workflowBlockId: block.id,
        payload: {
          title: data.block_title,
          ...getWorkflowActionTimingPayload(data),
          block_data: {
            category_id: data.category_id,
            survey_template_id: data.survey_template_id,
            when_to_save_responses: data.when_to_save_responses,
            public_title: data.public_title || null,
            public_description: data.public_description || null,
          },
          conditions: conditions.map((x) => ({ ...x })),
        },
      });

      analyticsService.trackEvent(analyticEvents.workflows.blockEdited, {
        [analyticProperties.id]: workflowVersion.workflow_id,
        [analyticProperties.actionType]: ApiWorkflowBlockActionTypes.survey_with_approval,
      });

      close(id);
    } catch {
      // handle error
    }
  });

  return (
    <Modal
      {...props}
      variant={"side"}
      title={t("workflow:action_type_approval")}
      layout={"stretch"}
      withCloser
      closeByEscEnabled
      size={"lg"}
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button
            size={"lg"}
            className={"w-full"}
            type={"submit"}
            onClick={handleSave}
            isLoading={updateBlockActionApproval.isPending}
          >
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={handleSave}>
        <Tabs onActiveTabChange={setActiveTab} activeTab={activeTab}>
          <TabItem title={t("common:message")}>
            <Stack className={"py-6"}>
              <Controller
                render={({ field: { value, onChange } }) => (
                  <Select
                    label={t("workflow:approval_category")}
                    emptyTitle={t("common:select")}
                    value={value}
                    required={true}
                    isLoading={isCategoriesLoading}
                    options={categoryOptions}
                    onChange={onChange}
                    error={errors.category_id?.message}
                  />
                )}
                control={control}
                name={"category_id"}
              />
              <Controller
                control={control}
                name={"survey_template_id"}
                render={({ field: { value, onChange } }) => (
                  <Select
                    label={t("workflow:approval_survey_template")}
                    emptyTitle={t("common:select")}
                    value={value}
                    required
                    searchable
                    isLoading={isTemplatesLoading}
                    options={templateOptions}
                    loadOptionsOnOpen
                    searchOptions={templateOptions}
                    onSearchOptions={(text) => {
                      setTemplateFilter((filter) => ({ ...filter, page_number: 1, text }));
                    }}
                    onChange={(templateId) => {
                      onChange(templateId);

                      const selectedTemplate = surveyTemplates?.items.find(
                        (template) => template.id === templateId
                      );
                      if (selectedTemplate) {
                        setValue("public_title", selectedTemplate.title, {
                          shouldDirty: true,
                        });
                      }
                    }}
                    error={errors.survey_template_id?.message}
                  />
                )}
              />
              <Controller
                control={control}
                name={"when_to_save_responses"}
                render={({ field: { value, onChange } }) => (
                  <Select
                    label={t("workflow:approval_when_to_save_responses")}
                    emptyTitle={t("common:select")}
                    value={value}
                    required
                    options={getWorkflowApprovalWriteResponsesTypeItems()}
                    onChange={onChange}
                    error={errors.when_to_save_responses?.message}
                  />
                )}
              />
              <Stack>
                <Title header={t("common:general_data")} size={"xs"} paddingTop paddingBottom />
                <Controller
                  control={control}
                  name={"public_title"}
                  render={({ field: { value, onChange, onBlur, ref } }) => (
                    <Input
                      label={t("common:title")}
                      type={"text"}
                      value={value ?? ""}
                      onChange={onChange}
                      onBlur={onBlur}
                      ref={ref}
                      error={errors.public_title?.message}
                    />
                  )}
                />
                <Textarea
                  label={t("common:description")}
                  {...register("public_description")}
                  error={errors.public_description?.message}
                />
              </Stack>
            </Stack>
          </TabItem>
          <TabItem title={t("workflow:conditions")}>
            <Stack gap={"lg"} className={"py-6"}>
              <Stack>
                <Title header={t("workflow:block")} size={"xs"} />
                <Input
                  label={t("common:title")}
                  type="text"
                  {...register("block_title")}
                  error={errors.block_title?.message}
                />
              </Stack>
              <WorkflowActionTimingEditor
                control={control}
                register={register}
                errors={errors}
                triggerDateAvailable={workflowVersion.trigger_schedule_rule !== null}
              />

              <WorkflowBlockConditionsList
                conditions={conditions}
                onAdd={handleAddCondition}
                onEdit={handleEditCondition}
                onDelete={handleDeleteCondition}
              />
            </Stack>
          </TabItem>
        </Tabs>
      </form>
    </Modal>
  );
}
